import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import DownloadOverlay from "./DownloadOverlay";

/**
 * MyProjects Tab (Product sourced from metadata.json)
 * --------------------------------------------------------------
 * - Project directories on disk are:  output-<nameNoExt>
 * - We load every directory the backend returns (ideally all output-*).
 * - Product type is read from: /outputs/<user>/<folder>/metadata.json → { "Product": "<type>" }
 * - Thumbnail: thumbnail.png (only; no GIF support)
 * - Download button now POSTs to /download/ with JSON { email, name }
 * - Admin users (verified via /verify-admin/) get a Publish / Unpublish button per card
 * - Publish opens an overlay with thumbnail + price + currency toggle and writes to published.json via backend
 * - Unpublish removes the project entry from published.json via backend
 * - Published projects show a 📋 button over the thumbnail (top-right)
 * - Clicking 📋 copies: <site-origin>/design/<admin name>/<full folder name>
 * - Buttons use minimal horizontal padding and dynamic font scaling to fill the row as much as possible
 * - Publish / Unpublish button reserves enough width for "Unpublish" so its size does not change when toggled
 */

const PAGE_SIZE = 10;
const KINDS = [
  { value: "all", label: "All types" },
  { value: "sign", label: "3D Sign" },
  { value: "keychain", label: "Keychain" },
  { value: "coaster", label: "Coaster" },
  { value: "stand", label: "Phone Stand" },
  { value: "lightbox", label: "Lightbox" },
  { value: "organizer", label: "Desk Organizer" },
  { value: "bookmark", label: "Bookmark" },
  { value: "cake topper", label: "Cake Topper" },
  { value: "standee", label: "Standee" },
  { value: "custom", label: "Custom Order Requests" },
  { value: "other", label: "Other" },
];

const SORTS = [
  { value: "updated_desc", label: "Last updated" },
  { value: "created_desc", label: "Newest" },
  { value: "created_asc", label: "Oldest" },
  { value: "name_asc", label: "Name A→Z" },
  { value: "name_desc", label: "Name Z→A" },
  { value: "size_desc", label: "Largest" },
  { value: "size_asc", label: "Smallest" },
];

const PRODUCT_COLORS = {
  sign: "#FF710A",
  keychain: "#14b8a6",
  coaster: "#a855f7",
  lightbox: "#db2777",
  "desk organizer": "#0011ffff",
  bookmark: "#ff0000ff",
  "cake topper": "#fad324ff",
};

function useDebounced(value, delay = 400) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

function defaultAPI(username) {
  return {
    async listProjects({ q, page, pageSize, sort, kind }) {
      if (!username) return { items: [], total: 0 };
      const params = new URLSearchParams({
        user: username,
        page: String(page),
        page_size: String(pageSize),
        sort,
      });
      if (q) params.set("q", q);

      if (kind && kind !== "all" && kind !== "custom") {
        params.set("kind", kind);
      }

      const res = await fetch(`/my-projects?${params.toString()}`);
      if (!res.ok) throw new Error(`Failed to load projects (${res.status})`);
      return res.json();
    },

    async deleteProject(id) {
      if (!username) throw new Error("No username");
      const res = await fetch(
        `/my-projects/${encodeURIComponent(id)}?user=${encodeURIComponent(username)}`,
        { method: "DELETE" }
      );
      if (!res.ok) throw new Error(`Delete failed (${res.status})`);
      return res.json();
    },
  };
}

function ActionButton({
  label,
  onClick,
  title,
  disabled,
  background = "#ffffff",
  color = "#111827",
  border = "1px solid #cbd5e1",
  padding = "4px 5px",
  fontSize = 12,
  minWidth,
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={title || label}
      style={{
        padding,
        fontSize,
        borderRadius: 6,
        border,
        background: disabled ? "#e5e7eb" : background,
        color: disabled ? "#9ca3af" : color,
        cursor: disabled ? "not-allowed" : "pointer",
        fontWeight: 500,
        whiteSpace: "nowrap",
        flex: "0 1 auto",
        minWidth: minWidth ?? 0,
        maxWidth: "100%",
        overflow: "hidden",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        lineHeight: 1.1,
        boxSizing: "border-box",
      }}
    >
      <span
        style={{
          display: "block",
          minWidth: 0,
          overflow: "hidden",
          whiteSpace: "nowrap",
          textOverflow: "clip",
          textAlign: "center",
        }}
      >
        {label}
      </span>
    </button>
  );
}

function toOutputFolderFromId(id) {
  if (!id) return null;
  if (id.startsWith("output-")) return id;
  const parts = id.split("-");
  if (parts.length >= 2) return `output-${parts.slice(1).join("-")}`;
  return `output-${id}`;
}

function withVersion(url, ver) {
  if (!url) return url;
  try {
    const u = new URL(url, window.location.origin);
    if (ver != null) u.searchParams.set("v", String(ver));
    return u.pathname + u.search + u.hash;
  } catch {
    return (
      url +
      (url.includes("?") ? "&" : "?") +
      "v=" +
      encodeURIComponent(String(ver ?? ""))
    );
  }
}

async function probeVersion(urls) {
  for (const url of urls) {
    if (!url) continue;
    try {
      const r = await fetch(url, { method: "HEAD", cache: "no-store" });
      if (!r.ok) continue;
      const lm = r.headers.get("last-modified");
      const etag = r.headers.get("etag");
      const len = r.headers.get("content-length");
      const ver = lm || etag || len;
      if (ver) return { url, ver };
      return { url, ver: "" };
    } catch {}
  }
  return null;
}

function stripTimestampTag(str) {
  if (!str) return str;
  return str.replace(/-(\d{8}[_-]\d{6})(?=\.[^.]+$|$)/, "");
}

function estimateTextWidth({
  text,
  fontSize,
  fontFamily = 'system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif',
  fontWeight = 500,
}) {
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) return text.length * fontSize * 0.6;
  ctx.font = `${fontWeight} ${fontSize}px ${fontFamily}`;
  return ctx.measureText(text).width;
}

function estimateRowFontSize({
  rowWidth,
  labels,
  fixedReservations = [],
  minFont = 10,
  maxFont = 18,
  gapPx = 4,
  padX = 5,
  borderPx = 2,
  fontFamily = 'system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif',
  fontWeight = 500,
}) {
  if (!rowWidth || rowWidth <= 0 || !labels.length) return 12;

  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) return 12;

  const totalGaps = gapPx * Math.max(0, labels.length - 1);

  const fits = (fontSize) => {
    ctx.font = `${fontWeight} ${fontSize}px ${fontFamily}`;

    let total = totalGaps;

    for (let i = 0; i < labels.length; i += 1) {
      const reserved = fixedReservations[i];
      if (typeof reserved === "number" && reserved > 0) {
        total += reserved;
      } else {
        const textW = ctx.measureText(labels[i]).width;
        total += Math.ceil(textW) + padX * 2 + borderPx;
      }
    }

    return total <= rowWidth;
  };

  let lo = minFont;
  let hi = maxFont;
  let best = minFont;

  while (lo <= hi) {
    const mid = Math.floor((lo + hi) / 2);
    if (fits(mid)) {
      best = mid;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }

  return best;
}

function resolveProjectFolder(project) {
  return (
    (project?.slug && `output-${project.slug}`) ||
    toOutputFolderFromId(project?.id) ||
    project?.id ||
    null
  );
}

function getDisplayProjectName(project) {
  return stripTimestampTag(project?.slug || project?.name || project?.id || "");
}

function PublishOverlay({
  visible,
  project,
  projectTitle,
  thumbnailSrc,
  defaultCurrency = "USD",
  defaultPrice = "",
  onClose,
  onConfirm,
  isSubmitting,
}) {
  const [price, setPrice] = useState(defaultPrice);
  const [currency, setCurrency] = useState(defaultCurrency);

  useEffect(() => {
    if (visible) {
      setPrice(defaultPrice ?? "");
      setCurrency(defaultCurrency || "USD");
    }
  }, [visible, defaultPrice, defaultCurrency, project]);

  if (!visible || !project) return null;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15,23,42,0.55)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 10000,
        padding: 20,
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 520,
          background: "#ffffff",
          borderRadius: 16,
          boxShadow: "0 10px 40px rgba(15,23,42,0.35)",
          padding: 20,
          position: "relative",
          display: "flex",
          flexDirection: "column",
          gap: 16,
        }}
      >
        <div
          onClick={onClose}
          style={{
            position: "absolute",
            top: 12,
            right: 12,
            fontSize: "3vh",
            width: "1.4em",
            height: "1.4em",
            padding: 0,
            boxSizing: "border-box",
            borderRadius: 8,
            background: "red",
            color: "#ffffff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            userSelect: "none",
            border: "0px solid #e2e8f0",
          }}
        >
          ×
        </div>

        <div>
          <div style={{ fontSize: 22, fontWeight: 700, color: "#111827" }}>
            Publish Design
          </div>
          <div style={{ fontSize: 14, color: "#6b7280", marginTop: 4 }}>
            {projectTitle}
          </div>
        </div>

        <div
          style={{
            width: "100%",
            aspectRatio: "1 / 1",
            borderRadius: 12,
            overflow: "hidden",
            background: "#f3f4f6",
            border: "1px solid #e5e7eb",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {thumbnailSrc ? (
            <img
              src={thumbnailSrc}
              alt={projectTitle}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />
          ) : (
            <div style={{ color: "#6b7280", fontSize: 13 }}>No thumbnail</div>
          )}
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <label
            style={{
              fontSize: 14,
              fontWeight: 600,
              color: "#111827",
            }}
          >
            Price
          </label>

          <div style={{ display: "flex", gap: 10, alignItems: "stretch" }}>
            <input
              type="number"
              min="0"
              step="0.01"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              placeholder="Enter price"
              style={{
                flex: 1,
                padding: "10px 12px",
                borderRadius: 10,
                border: "1px solid #cbd5e1",
                outline: "none",
                fontSize: 15,
              }}
            />

            <div
              style={{
                display: "flex",
                border: "1px solid #cbd5e1",
                borderRadius: 10,
                overflow: "hidden",
                minWidth: 120,
              }}
            >
              <button
                type="button"
                onClick={() => setCurrency("USD")}
                style={{
                  flex: 1,
                  padding: "10px 0",
                  border: "none",
                  background: currency === "USD" ? "#16a34a" : "#ffffff",
                  color: currency === "USD" ? "#ffffff" : "#111827",
                  cursor: "pointer",
                  fontWeight: 600,
                }}
              >
                USD
              </button>
              <button
                type="button"
                onClick={() => setCurrency("EUR")}
                style={{
                  flex: 1,
                  padding: "10px 0",
                  border: "none",
                  borderLeft: "1px solid #cbd5e1",
                  background: currency === "EUR" ? "#16a34a" : "#ffffff",
                  color: currency === "EUR" ? "#ffffff" : "#111827",
                  cursor: "pointer",
                  fontWeight: 600,
                }}
              >
                EUR
              </button>
            </div>
          </div>
        </div>

        <button
          type="button"
          disabled={isSubmitting || String(price).trim() === ""}
          onClick={() =>
            onConfirm?.({
              price,
              currency,
            })
          }
          style={{
            marginTop: 4,
            padding: "12px 14px",
            borderRadius: 10,
            border: "1px solid #16a34a",
            background:
              isSubmitting || String(price).trim() === "" ? "#86efac" : "#16a34a",
            color: "#ffffff",
            fontSize: 15,
            fontWeight: 700,
            cursor:
              isSubmitting || String(price).trim() === "" ? "not-allowed" : "pointer",
          }}
        >
          {isSubmitting ? "Publishing..." : "Publish"}
        </button>
      </div>
    </div>
  );
}

function ProjectCard({
  project,
  onOpen,
  onDelete,
  onReview,
  refreshKey,
  isAdmin,
  isPublished,
  onTogglePublish,
  onCopyPublishedLink,
}) {
  const [imgError, setImgError] = useState(false);
  const [imgSrc, setImgSrc] = useState(null);
  const [productFromMeta, setProductFromMeta] = useState(
    project.productName || "Project"
  );
  const [resolvedFolder, setResolvedFolder] = useState(null);
  const [isCheckingZip, setIsCheckingZip] = useState(false);
  const [actionFontSize, setActionFontSize] = useState(12);

  const rowRef = useRef(null);

  const isCustom = !!project.isCustom;

  const fromLabel = useMemo(() => {
    return (
      project.customerEmail ||
      project.customer_email ||
      project.customerName ||
      project.customer_name ||
      null
    );
  }, [project]);

  const productColor = useMemo(() => {
    const key = (productFromMeta || "").toString().trim().toLowerCase();
    return PRODUCT_COLORS[key] || "#FF710A";
  }, [productFromMeta]);

  const userDir = useMemo(() => {
    if (project.previewUrl) {
      const m = project.previewUrl.match(/^\/outputs\/([^/]+)\//);
      if (m) return m[1];
    }
    const u = project.user || "";
    return u.includes("@") ? u.split("@")[0] : u;
  }, [project.previewUrl, project.user]);

  const candidateFolders = useMemo(() => {
    const c = [];
    if (project?.slug) c.push(`output-${project.slug}`);
    const fromId = toOutputFolderFromId(project?.id);
    if (fromId) c.push(fromId);
    if (project?.id) c.push(project.id);
    return Array.from(new Set(c));
  }, [project?.slug, project?.id]);

  useEffect(() => {
    if (!userDir || candidateFolders.length === 0) return;
    let cancelled = false;

    const headOk = async (url) => {
      try {
        const r = await fetch(url, { method: "HEAD", cache: "no-store" });
        return r.ok;
      } catch {
        return false;
      }
    };

    (async () => {
      for (const folder of candidateFolders) {
        const base = `/outputs/${userDir}/${folder}`;
        if (
          (await headOk(`${base}/thumbnail.png`)) ||
          (await headOk(`${base}/metadata.json`))
        ) {
          if (!cancelled) setResolvedFolder(folder);
          return;
        }
      }
      if (!cancelled) setResolvedFolder(candidateFolders[0]);
    })();

    return () => {
      cancelled = true;
    };
  }, [userDir, candidateFolders]);

  const rawPngUrl = useMemo(() => {
    if (project.previewUrl && /\.png(\?.*)?$/i.test(project.previewUrl)) {
      return project.previewUrl;
    }
    if (!userDir || !resolvedFolder) return null;
    return `/outputs/${userDir}/${resolvedFolder}/thumbnail.png`;
  }, [project.previewUrl, userDir, resolvedFolder]);

  useEffect(() => {
    let cancelled = false;
    if (!rawPngUrl) return;

    (async () => {
      const probed = await probeVersion([rawPngUrl]);
      if (cancelled) return;

      if (probed) {
        setImgSrc(withVersion(probed.url, probed.ver ?? ""));
        setImgError(false);
      } else {
        setImgSrc(withVersion(rawPngUrl, ""));
        setImgError(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [rawPngUrl, refreshKey]);

  const handleImgError = () => {
    setImgError(true);
    setImgSrc(null);
  };

  useEffect(() => {
    if (!userDir || !resolvedFolder) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `/outputs/${userDir}/${resolvedFolder}/metadata.json`,
          { cache: "no-store" }
        );
        if (!res.ok) return;
        const data = await res.json();
        const prod = (data?.Product || data?.product || "").toString().trim();
        if (!cancelled && prod) setProductFromMeta(prod);
      } catch {}
    })();
    return () => {
      cancelled = true;
    };
  }, [userDir, resolvedFolder, refreshKey]);

  const displayTitleRaw = useMemo(() => {
    if (project.slug) return project.slug;
    if (project.name) return project.name;
    if (resolvedFolder?.startsWith("output-")) {
      return resolvedFolder.slice("output-".length);
    }
    return project.id || "project";
  }, [project.slug, project.name, project.id, resolvedFolder]);

  const displayTitle = useMemo(
    () => stripTimestampTag(displayTitleRaw),
    [displayTitleRaw]
  );

  const handleDownload = async () => {
    if (!project?.user || !resolvedFolder) {
      alert("Missing project info for download.");
      return;
    }
    setIsCheckingZip(true);
    try {
      const res = await fetch("/download/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: project.user,
          name: resolvedFolder,
        }),
      });

      if (!res.ok) {
        const msg = await res.text().catch(() => "");
        throw new Error(msg || `Download failed (${res.status})`);
      }

      const disp =
        res.headers.get("Content-Disposition") ||
        res.headers.get("content-disposition") ||
        "";
      let filename =
        (disp.match(/filename\*=UTF-8''([^;]+)/)?.[1] &&
          decodeURIComponent(disp.match(/filename\*=UTF-8''([^;]+)/)[1])) ||
        disp.match(/filename="?([^"]+)"?/)?.[1] ||
        `${displayTitle || "project"}.zip`;

      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      alert(e?.message || "Download is not available for this project yet.");
    } finally {
      setIsCheckingZip(false);
    }
  };

  const buttonLabels = useMemo(() => {
    const labels = [isCustom ? "Review" : "Edit", "Delete"];
    if (!isCustom) labels.push(isCheckingZip ? "Downloading…" : "Download");
    if (isAdmin) labels.push(isPublished ? "Unpublish" : "Publish");
    return labels;
  }, [isCustom, isCheckingZip, isAdmin, isPublished]);

  useLayoutEffect(() => {
    const compute = () => {
      if (!rowRef.current) return;
      const rowWidth = rowRef.current.clientWidth;
      const fixedReservations = buttonLabels.map((label) => {
        if (label === "Publish" || label === "Unpublish") {
          return (
            Math.ceil(
              estimateTextWidth({
                text: "Unpublish",
                fontSize: 12,
              })
            ) + 5 * 2 + 2
          );
        }
        return 0;
      });

      const nextSize = estimateRowFontSize({
        rowWidth,
        labels: buttonLabels,
        fixedReservations,
        minFont: 10,
        maxFont: 18,
        gapPx: 4,
        padX: 5,
        borderPx: 2,
      });
      setActionFontSize(nextSize);
    };

    compute();

    let ro;
    if (typeof ResizeObserver !== "undefined" && rowRef.current) {
      ro = new ResizeObserver(() => {
        compute();
      });
      ro.observe(rowRef.current);
    }

    window.addEventListener("resize", compute);
    return () => {
      window.removeEventListener("resize", compute);
      if (ro) ro.disconnect();
    };
  }, [buttonLabels]);

  const compactPadding = "4px 5px";
  const publishMinWidth = useMemo(() => {
    const textWidth = estimateTextWidth({
      text: "Unpublish",
      fontSize: actionFontSize,
    });
    return Math.ceil(textWidth) + 5 * 2 + 2;
  }, [actionFontSize]);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        border: "1px solid #e5e7eb",
        borderRadius: 12,
        overflow: "hidden",
        background: "#ffffff",
        boxShadow: "0 1px 2px rgba(219, 117, 21, 0.04)",
      }}
    >
      <div
        style={{
          position: "relative",
          background: "#f8fafc",
          aspectRatio: "1 / 1",
        }}
      >
        {imgSrc && !imgError ? (
          <img
            src={imgSrc}
            alt={displayTitle}
            loading="lazy"
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
            onError={handleImgError}
          />
        ) : (
          <div
            style={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 12,
              color: "#6b7280",
            }}
          >
            No <code>thumbnail.png</code>
          </div>
        )}

        <div
          style={{
            position: "absolute",
            top: 8,
            left: 8,
            display: "flex",
            gap: 6,
            flexWrap: "wrap",
          }}
        >
          <span
            style={{
              padding: "1px 8px",
              borderRadius: 999,
              fontSize: 12,
              background: productColor,
              color: "white",
              opacity: 0.95,
              textTransform: "capitalize",
              maxWidth: 140,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {productFromMeta}
          </span>
          {isCustom && (
            <span
              style={{
                padding: "1px 8px",
                borderRadius: 999,
                fontSize: 12,
                background: "#16a34a",
                color: "white",
                opacity: 0.95,
                textTransform: "none",
                whiteSpace: "nowrap",
              }}
            >
              Custom Request
            </span>
          )}
        </div>

        {isAdmin && isPublished && !isCustom && (
          <button
            type="button"
            onClick={() => onCopyPublishedLink?.(project)}
            title="Copy public design link"
            style={{
              position: "absolute",
              top: 8,
              right: 8,
              width: 34,
              height: 34,
              borderRadius: 999,
              border: "1px solid rgba(255,255,255,0.7)",
              background: "rgba(17,24,39,0.82)",
              color: "#ffffff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              fontSize: 18,
              lineHeight: 1,
              boxShadow: "0 2px 10px rgba(0,0,0,0.22)",
              backdropFilter: "blur(2px)",
            }}
          >
            📋
          </button>
        )}

        {fromLabel && (
          <div
            style={{
              position: "absolute",
              bottom: 8,
              left: 8,
              background: "rgba(99, 99, 99, 0.85)",
              color: "white",
              padding: "3px 8px",
              borderRadius: 999,
              fontSize: 11,
              whiteSpace: "nowrap",
              maxWidth: "90%",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
            title={`From: ${fromLabel}`}
          >
            From: {fromLabel}
          </div>
        )}
      </div>

      <div
        style={{ padding: 12, display: "flex", flexDirection: "column", gap: 8 }}
      >
        <div
          style={{
            fontWeight: 600,
            fontSize: 16,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          title={displayTitle}
        >
          {displayTitle}
        </div>

        <div
          ref={rowRef}
          style={{
            display: "flex",
            flexDirection: "row",
            gap: 4,
            alignItems: "center",
            justifyContent: "center",
            flexWrap: "nowrap",
            width: "100%",
            minWidth: 0,
          }}
        >
          <ActionButton
            label={isCustom ? "Review" : "Edit"}
            onClick={() => (isCustom ? onReview?.(project) : onOpen(project))}
            title={isCustom ? "Review custom request" : "Open in designer"}
            padding={compactPadding}
            fontSize={actionFontSize}
          />

          <ActionButton
            label="Delete"
            onClick={() => onDelete(project)}
            padding={compactPadding}
            fontSize={actionFontSize}
          />

          {!isCustom && (
            <ActionButton
              label={isCheckingZip ? "Downloading…" : "Download"}
              onClick={handleDownload}
              title="Download ZIP"
              disabled={!project?.user || !resolvedFolder || isCheckingZip}
              padding={compactPadding}
              fontSize={actionFontSize}
            />
          )}

          {isAdmin && (
            <ActionButton
              label={isPublished ? "Unpublish" : "Publish"}
              onClick={() => onTogglePublish?.(project)}
              title={
                isPublished
                  ? "Remove this design from published.json"
                  : "Open publish overlay"
              }
              background={isPublished ? "#dc2626" : "#16a34a"}
              color="#ffffff"
              border={isPublished ? "1px solid #dc2626" : "1px solid #16a34a"}
              padding={compactPadding}
              fontSize={actionFontSize}
              minWidth={publishMinWidth}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default function MyProjects({
  username,
  onOpenProject,
  api: apiProp,
  onCreditsUpdate,
}) {
  const api = useMemo(() => apiProp || defaultAPI(username), [apiProp, username]);

  const [q, setQ] = useState("");
  const [kind, setKind] = useState("all");
  const [sort, setSort] = useState("updated_desc");
  const [page, setPage] = useState(1);
  const debouncedQ = useDebounced(q, 350);

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);

  const [reviewProject, setReviewProject] = useState(null);
  const [showCustomCreditOverlay, setShowCustomCreditOverlay] = useState(false);

  const [isAdmin, setIsAdmin] = useState(false);
  const [adminCheckDone, setAdminCheckDone] = useState(false);
  const [publishStateById, setPublishStateById] = useState({});

  const [publishOverlayProject, setPublishOverlayProject] = useState(null);
  const [publishOverlayThumb, setPublishOverlayThumb] = useState(null);
  const [isSubmittingPublish, setIsSubmittingPublish] = useState(false);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const getFullProjectKey = (project) =>
    String(resolveProjectFolder(project) || "");

  const getDisplayProjectKey = (project) =>
    String(getDisplayProjectName(project));

  const getAdminNameForUrl = useMemo(() => {
    const raw = (username || "").trim();
    if (!raw) return "";
    return raw.includes("@") ? raw.split("@")[0] : raw;
  }, [username]);

  useEffect(() => {
    let cancelled = false;

    const verifyAdmin = async () => {
      if (!username) {
        if (!cancelled) {
          setIsAdmin(false);
          setAdminCheckDone(true);
        }
        return;
      }

      try {
        setAdminCheckDone(false);

        const res = await fetch("/verify-admin/", {
          method: "GET",
          headers: {
            user: username,
          },
        });

        if (!cancelled) {
          setIsAdmin(res.ok);
          setAdminCheckDone(true);
        }
      } catch {
        if (!cancelled) {
          setIsAdmin(false);
          setAdminCheckDone(true);
        }
      }
    };

    verifyAdmin();

    return () => {
      cancelled = true;
    };
  }, [username]);

  useEffect(() => {
    if (!username) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const res = await api.listProjects({
          q: debouncedQ,
          page,
          pageSize: PAGE_SIZE,
          sort,
          kind,
        });
        if (!cancelled) {
          setItems(res.items || []);
          setTotal(res.total || 0);
        }
      } catch (e) {
        if (!cancelled) setError(e?.message || "Failed to load projects");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api, debouncedQ, page, sort, kind, refreshKey, username]);

  useEffect(() => {
    let cancelled = false;

    const loadPublishedState = async () => {
      if (!username || !isAdmin) return;

      try {
        const res = await fetch(
          `/published-projects/?user=${encodeURIComponent(username)}`,
          {
            method: "GET",
            headers: {
              user: username,
            },
            cache: "no-store",
          }
        );

        if (!res.ok) return;

        const data = await res.json();
        const published = data?.published || {};
        const next = {};

        Object.keys(published).forEach((projectName) => {
          next[String(projectName)] = true;
        });

        if (!cancelled) {
          setPublishStateById(next);
        }
      } catch {}
    };

    loadPublishedState();

    return () => {
      cancelled = true;
    };
  }, [username, isAdmin, refreshKey]);

  async function handleDelete(project) {
    const nameForPrompt = getDisplayProjectKey(project);
    if (
      !window.confirm(
        `Delete project "${nameForPrompt}"? This cannot be undone.`
      )
    ) {
      return;
    }

    const prev = items;
    setItems(prev.filter((p) => p.id !== project.id));

    try {
      let userForDelete = username;

      if (project.isCustom && project.previewUrl) {
        const m = project.previewUrl.match(/^\/outputs\/([^/]+)\//);
        if (m && m[1]) {
          userForDelete = m[1];
        }
      }

      const res = await fetch(
        `/my-projects/${encodeURIComponent(project.id)}?user=${encodeURIComponent(
          userForDelete
        )}`,
        { method: "DELETE" }
      );

      if (!res.ok) {
        const msg = await res.text().catch(() => "");
        throw new Error(msg || `Delete failed (${res.status})`);
      }

      setTotal((t) => Math.max(0, t - 1));
      setRefreshKey((k) => k + 1);
    } catch (e) {
      setItems(prev);
      alert(e?.message || "Delete failed");
    }
  }

  function EmptyState() {
    return (
      <div
        style={{
          textAlign: "center",
          padding: "60px 20px",
          color: "#6b7280",
        }}
      >
        <div
          style={{
            fontSize: 20,
            fontWeight: 600,
            marginBottom: 6,
          }}
        >
          No projects yet
        </div>
        <div style={{ fontSize: 14 }}>
          Lets create some cool designs via one of the tabs above!
        </div>
      </div>
    );
  }

  const handleOpenReview = (project) => {
    setReviewProject(project);
  };

  const handleAcceptReview = async () => {
    if (!reviewProject) return;

    try {
      const res = await fetch("/deduct-credit-custom/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: username,
        }),
      });

      if (!res.ok) {
        const msg = await res.text().catch(() => "");
        alert(msg || "Failed to deduct credits.");
        return;
      }

      const data = await res.json();

      if (data && typeof data.credits === "number") {
        onCreditsUpdate?.(data.credits);
      }

      let fsRoot = username;
      if (reviewProject.previewUrl) {
        const m = reviewProject.previewUrl.match(/^\/outputs\/([^/]+)\//);
        if (m && m[1]) fsRoot = m[1];
      }

      const moveRes = await fetch("/move-custom-project/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          fromUser: fsRoot,
          toUser: username,
          folder: reviewProject.id,
        }),
      });

      if (!moveRes.ok) {
        const msg = await moveRes.text().catch(() => "");
        alert(msg || "Failed to move custom project folder.");
        return;
      }

      setRefreshKey((k) => k + 1);
      setReviewProject(null);
    } catch (err) {
      alert(err?.message || "Something went wrong.");
    }
  };

  const handleRejectReview = async () => {
    if (!reviewProject) return;
    await handleDelete(reviewProject);
    setReviewProject(null);
  };

  const resolveProjectThumbnail = (project) => {
    if (project?.previewUrl && /\.png(\?.*)?$/i.test(project.previewUrl)) {
      return project.previewUrl;
    }

    const userDir =
      project?.previewUrl?.match(/^\/outputs\/([^/]+)\//)?.[1] ||
      (username?.includes("@") ? username.split("@")[0] : username);

    const folder = resolveProjectFolder(project);

    if (!userDir || !folder) return null;
    return `/outputs/${userDir}/${folder}/thumbnail.png`;
  };

  const handleTogglePublish = async (project) => {
    const projectFullKey = getFullProjectKey(project);
    const projectDisplayKey = getDisplayProjectKey(project);

    if (!projectFullKey) return;

    const currentlyPublished = !!publishStateById[projectFullKey];

    if (currentlyPublished) {
      const confirmed = window.confirm(
        `Unpublish "${projectDisplayKey}"? This will remove it from published.json.`
      );
      if (!confirmed) return;

      try {
        const res = await fetch("/unpublish-project/", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            user: username,
          },
          body: JSON.stringify({
            user: username,
            projectName: projectFullKey,
          }),
        });

        if (!res.ok) {
          const msg = await res.text().catch(() => "");
          throw new Error(msg || `Unpublish failed (${res.status})`);
        }

        setPublishStateById((prev) => {
          const next = { ...prev };
          delete next[projectFullKey];
          return next;
        });

        setRefreshKey((k) => k + 1);
      } catch (e) {
        alert(e?.message || "Failed to unpublish project.");
      }

      return;
    }

    setPublishOverlayThumb(resolveProjectThumbnail(project));
    setPublishOverlayProject(project);
  };

  const handleConfirmPublish = async ({ price, currency }) => {
    if (!publishOverlayProject) return;

    const projectFullKey = getFullProjectKey(publishOverlayProject);
    if (!projectFullKey) {
      alert("Missing project directory name.");
      return;
    }

    setIsSubmittingPublish(true);

    try {
      const res = await fetch("/publish-project/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          user: username,
        },
        body: JSON.stringify({
          user: username,
          projectName: projectFullKey,
          paid: false,
          price: Number(price),
          Currency: currency,
        }),
      });

      if (!res.ok) {
        const msg = await res.text().catch(() => "");
        throw new Error(msg || `Publish failed (${res.status})`);
      }

      setPublishStateById((prev) => ({
        ...prev,
        [projectFullKey]: true,
      }));

      setPublishOverlayProject(null);
      setPublishOverlayThumb(null);
      setRefreshKey((k) => k + 1);
    } catch (e) {
      alert(e?.message || "Failed to publish project.");
    } finally {
      setIsSubmittingPublish(false);
    }
  };

  const handleCopyPublishedLink = async (project) => {
    const designName = encodeURIComponent(getFullProjectKey(project));
    const adminName = encodeURIComponent(getAdminNameForUrl);
    const base = window.location.origin.replace(/\/$/, "");
    const publicUrl = `${base}/design/${adminName}/${designName}`;

    const fallbackCopyText = (text) => {
      const textArea = document.createElement("textarea");
      textArea.value = text;
      textArea.setAttribute("readonly", "");
      textArea.style.position = "fixed";
      textArea.style.top = "-9999px";
      textArea.style.left = "-9999px";
      textArea.style.opacity = "0";
      document.body.appendChild(textArea);

      const isIOS = /ipad|iphone|ipod/i.test(navigator.userAgent);

      if (isIOS) {
        const range = document.createRange();
        range.selectNodeContents(textArea);
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);
        textArea.setSelectionRange(0, 999999);
      } else {
        textArea.focus();
        textArea.select();
      }

      let copied = false;
      try {
        copied = document.execCommand("copy");
      } catch {
        copied = false;
      }

      document.body.removeChild(textArea);
      return copied;
    };

    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(publicUrl);
        return;
      }

      const copied = fallbackCopyText(publicUrl);
      if (copied) {
        return;
      }

      if (navigator.share) {
        try {
          await navigator.share({
            title: "Public Design Link",
            url: publicUrl,
          });
          return;
        } catch {
          // fall through silently
        }
      }
    } catch {
      try {
        const copied = fallbackCopyText(publicUrl);
        if (copied) {
          return;
        }
      } catch {}

      if (navigator.share) {
        try {
          await navigator.share({
            title: "Public Design Link",
            url: publicUrl,
          });
          return;
        } catch {
          // fall through silently
        }
      }
    }
  };

  const getPublishStateForProject = (project) => {
    const key = getFullProjectKey(project);
    return !!publishStateById[key];
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div
        style={{
          display: "flex",
          gap: 8,
          alignItems: "center",
          padding: 10,
          background: "#f8fafc",
          borderBottom: "1px solid #e5e7eb",
          flexWrap: "wrap",
          position: "relative",
          zIndex: 20,
        }}
      >
        <div
          style={{
            fontWeight: 700,
            fontSize: 18,
            marginRight: 8,
            position: "relative",
            zIndex: 21,
          }}
        >
          My Projects
        </div>

        {adminCheckDone && isAdmin && (
          <div
            style={{
              padding: "6px 10px",
              borderRadius: 999,
              background: "#dcfce7",
              color: "#166534",
              fontSize: 12,
              fontWeight: 700,
              border: "1px solid #86efac",
              position: "relative",
              zIndex: 21,
            }}
            title="Admin verified"
          >
            Admin Mode
          </div>
        )}

        <input
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setPage(1);
          }}
          placeholder="Search by filename…"
          style={{
            flex: 1,
            minWidth: 220,
            padding: "8px 10px",
            borderRadius: 8,
            border: "1px solid #cbd5e1",
            outline: "none",
            position: "relative",
            zIndex: 21,
          }}
        />

        <select
          value={kind}
          onChange={(e) => {
            const next = e.target.value;
            setTimeout(() => {
              setKind(next);
              setPage(1);
            }, 0);
          }}
          title="Filter by type"
          style={{
            padding: "8px 10px",
            borderRadius: 8,
            border: "1px solid #cbd5e1",
            cursor: "pointer",
            position: "relative",
            zIndex: 21,
          }}
        >
          {KINDS.map((k) => (
            <option key={k.value} value={k.value}>
              {k.label}
            </option>
          ))}
        </select>

        <select
          value={sort}
          onChange={(e) => {
            const next = e.target.value;
            setTimeout(() => {
              setSort(next);
              setPage(1);
            }, 0);
          }}
          title="Sort"
          style={{
            padding: "8px 10px",
            borderRadius: 8,
            border: "1px solid #cbd5e1",
            cursor: "pointer",
            position: "relative",
            zIndex: 21,
          }}
        >
          {SORTS.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </select>

        <button
          onClick={() => setRefreshKey((k) => k + 1)}
          style={{
            padding: "8px 12px",
            borderRadius: 8,
            border: "1px solid #cbd5e1",
            background: "#ffffff",
            cursor: "pointer",
            position: "relative",
            zIndex: 21,
          }}
        >
          Refresh
        </button>
      </div>

      <div style={{ padding: 12, flex: 1, overflow: "auto" }}>
        {error && (
          <div
            style={{
              marginBottom: 10,
              padding: 10,
              border: "1px solid #fecaca",
              background: "#fef2f2",
              color: "#991b1b",
              borderRadius: 8,
            }}
          >
            {error}
          </div>
        )}

        {loading ? (
          <div style={{ textAlign: "center", padding: 40, color: "#6b7280" }}>
            Loading…
          </div>
        ) : items.length === 0 ? (
          <EmptyState />
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
              gap: 12,
            }}
          >
            {items
              .filter((p) => {
                if (kind === "custom") return !!p.isCustom;
                return true;
              })
              .map((p) => (
                <ProjectCard
                  key={`${p.id}-${refreshKey}`}
                  project={{ ...p, user: username }}
                  onOpen={(proj) => onOpenProject?.(proj)}
                  onDelete={handleDelete}
                  onReview={handleOpenReview}
                  refreshKey={refreshKey}
                  isAdmin={isAdmin}
                  isPublished={getPublishStateForProject(p)}
                  onTogglePublish={handleTogglePublish}
                  onCopyPublishedLink={handleCopyPublishedLink}
                />
              ))}
          </div>
        )}
      </div>

      {totalPages > 1 && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 8,
            padding: 10,
            borderTop: "1px solid #e5e7eb",
            background: "#fafafa",
          }}
        >
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            style={{
              padding: "6px 10px",
              borderRadius: 6,
              border: "1px solid #cbd5e1",
              background: page <= 1 ? "#e5e7eb" : "#ffffff",
              cursor: page <= 1 ? "not-allowed" : "pointer",
            }}
          >
            Prev
          </button>
          <div style={{ fontSize: 13, color: "#374151" }}>
            Page <strong>{page}</strong> of <strong>{totalPages}</strong>
          </div>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
            style={{
              padding: "6px 10px",
              borderRadius: 6,
              border: "1px solid #cbd5e1",
              background: page >= totalPages ? "#e5e7eb" : "#ffffff",
              cursor: page >= totalPages ? "not-allowed" : "pointer",
            }}
          >
            Next
          </button>
        </div>
      )}

      {reviewProject && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15,23,42,0.55)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <div
            style={{
              background: "#ffffff",
              borderRadius: 16,
              padding: 20,
              width: "96vw",
              maxWidth: "900px",
              maxHeight: "90vh",
              display: "flex",
              flexDirection: "column",
              overflowY: "auto",
              boxShadow: "0 10px 40px rgba(15,23,42,0.35)",
              position: "relative",
            }}
          >
            <div
              onClick={() => setReviewProject(null)}
              style={{
                position: "absolute",
                top: 12,
                right: 12,
                fontSize: "3vh",
                width: "1.4em",
                height: "1.4em",
                padding: 0,
                boxSizing: "border-box",
                borderRadius: 8,
                background: "red",
                color: "#ffffff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                userSelect: "none",
                border: "0px solid #e2e8f0",
              }}
            >
              ×
            </div>

            <div style={{ marginBottom: 12 }}>
              <div
                style={{
                  fontSize: 18,
                  fontWeight: 600,
                  marginBottom: 0,
                }}
              >
                Review Custom Request
              </div>
              <div
                style={{
                  fontSize: 13,
                  color: "#4b5563",
                }}
              >
                {getDisplayProjectKey(reviewProject)}
              </div>
            </div>

            {reviewProject.previewUrl && (
              <div
                style={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                  margin: "0px 0",
                }}
              >
                <div
                  style={{
                    width: "100%",
                    maxWidth: "min(88vw, 64vh)",
                    aspectRatio: "1 / 1",
                    borderRadius: 16,
                    overflow: "hidden",
                    background: "#f3f4f6",
                    border: "1px solid #e5e7eb",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <img
                    src={reviewProject.previewUrl}
                    alt="Project preview"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                </div>
              </div>
            )}

            {(reviewProject.customerEmail || reviewProject.customerNotes) && (
              <div
                style={{
                  marginTop: 8,
                  marginBottom: 8,
                  padding: 10,
                  borderRadius: 8,
                  background: "#f9fafb",
                  border: "1px solid #e5e7eb",
                  fontSize: 13,
                  color: "#374151",
                }}
              >
                {reviewProject.customerEmail && (
                  <div style={{ marginBottom: 4 }}>
                    <strong>Customer Email:</strong>{" "}
                    {reviewProject.customerEmail}
                  </div>
                )}
                {reviewProject.customerNotes && (
                  <div>
                    <strong>Notes:</strong> {reviewProject.customerNotes}
                  </div>
                )}
              </div>
            )}

            <div style={{ flex: 1 }} />

            <div
              style={{
                display: "flex",
                justifyContent: "center",
                gap: 12,
                marginTop: 10,
              }}
            >
              <button
                onClick={() => setShowCustomCreditOverlay(true)}
                style={{
                  padding: "8px 14px",
                  borderRadius: 8,
                  border: "1px solid #16a34a",
                  background: "#16a34a",
                  color: "white",
                  fontSize: 13,
                  fontWeight: 500,
                  cursor: "pointer",
                }}
              >
                Accept
              </button>

              <button
                onClick={handleRejectReview}
                style={{
                  padding: "8px 14px",
                  borderRadius: 8,
                  border: "1px solid #dc2626",
                  background: "#ffffff",
                  color: "#dc2626",
                  fontSize: 13,
                  fontWeight: 500,
                  cursor: "pointer",
                }}
              >
                Reject
              </button>
            </div>
          </div>
        </div>
      )}

      <PublishOverlay
        visible={!!publishOverlayProject}
        project={publishOverlayProject}
        projectTitle={getDisplayProjectKey(publishOverlayProject)}
        thumbnailSrc={publishOverlayThumb}
        defaultCurrency="USD"
        defaultPrice=""
        isSubmitting={isSubmittingPublish}
        onClose={() => {
          if (isSubmittingPublish) return;
          setPublishOverlayProject(null);
          setPublishOverlayThumb(null);
        }}
        onConfirm={handleConfirmPublish}
      />

      <DownloadOverlay
        visible={!!reviewProject && showCustomCreditOverlay}
        onClose={() => setShowCustomCreditOverlay(false)}
        onConfirm={async () => {
          await handleAcceptReview();
          setShowCustomCreditOverlay(false);
        }}
        username={null}
        project={null}
        credits={3}
        extra={{ source: "custom_accept" }}
      />
    </div>
  );
}